import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import Button from '../../../core/components/Button.tsx';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import DropdownMenu from '../../../core/components/DropdownMenu.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import {
    convertFieldsToEnabledFieldInputs,
    FieldDefinition,
    FieldInput,
    FieldOption,
    isGroupField,
} from '../../../document/components/fieldsConfigData.ts';
import FieldsPage, {
    FieldsDocumentConfiguration,
    FieldsSection,
    HEADER_SECTION,
} from '../../../document/components/FieldsPage.tsx';
import { GROUP_SEPARATOR } from '../../../document/pages/Assistance/useDocumentConfig';
import UniversalItemTypeFormModal from './UniversalItemTypeFormModal.tsx';

const AddIcon = withIcon(faPlus);

// universal fields start required, and without choices: the universal extraction doesn't read them
const UNIVERSAL_CUSTOM_FIELD_OPTIONS: FieldOption[] = [
    { name: 'is_required', valueType: 'boolean', defaultValue: true },
    { name: 'data_label_name', valueType: 'string', defaultValue: null },
    { name: 'data_type', valueType: 'string', defaultValue: 'string' },
    { name: 'field_label_name', valueType: 'string', defaultValue: null },
    { name: 'data_label_instructions', valueType: 'string', defaultValue: null },
];

const optionValue = (field: FieldDefinition, name: string) =>
    field.options?.find((option) => option.name === name)?.value as string | undefined;
const tableLabelOf = (table: FieldDefinition) => optionValue(table, 'field_label_name') ?? table.name;

interface TableModal {
    kind: 'add' | 'rename' | 'delete';
    table?: FieldDefinition;
}

interface UniversalFieldsConfigProps {
    channel: { documentConfig?: { fields?: FieldDefinition[] } } | undefined;
    channelId: string;
    documentConfiguration: FieldsDocumentConfiguration;
    handlers: { onUpdateDocumentConfig: (data: { fieldConfigs: FieldInput[] }) => Promise<unknown> };
}

// the Fields tab of a universal channel: the header fields, then a section per table data section, each with its
// own ⋯ to rename or delete the table
const UniversalFieldsConfig = ({ channel, channelId, documentConfiguration, handlers }: UniversalFieldsConfigProps) => {
    const { t } = useTranslation('config');
    const { publishToast } = useToaster();
    const [tableModal, setTableModal] = useState<TableModal | null>(null);

    const config = channel?.documentConfig;
    const fields = config?.fields ?? [];
    const tables = fields.filter(isGroupField).sort((a, b) => tableLabelOf(a).localeCompare(tableLabelOf(b)));
    const tableNames = tables.map((table) => table.name);

    const updateFields = (fieldConfigs: FieldInput[]) => handlers.onUpdateDocumentConfig({ fieldConfigs });

    // a table's fields outlive a table deleted before this was built; they go with the next save
    const handleSubmit = (fieldConfigs: FieldInput[]) =>
        updateFields(
            fieldConfigs.filter(
                ({ fieldName }) =>
                    !fieldName.includes(GROUP_SEPARATOR) || tableNames.includes(fieldName.split(GROUP_SEPARATOR)[0])
            )
        );

    const updateTables = async (fieldConfigs: FieldInput[]) => {
        try {
            await updateFields(fieldConfigs);
            setTableModal(null);
            publishToast({ description: t('configPage.document.updateSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('configPage.document.updateError'), status: 'error' });
        }
    };

    const handleAddTable = ({ tableDataLabelName, tableFieldLabelName }) =>
        updateTables([
            ...convertFieldsToEnabledFieldInputs(fields),
            {
                fieldName: tableDataLabelName,
                options: [
                    { name: 'data_label_name', value: tableDataLabelName },
                    { name: 'field_label_name', value: tableFieldLabelName },
                    { name: 'group', value: true },
                ],
            },
        ]);

    const handleRenameTable = (table: FieldDefinition, tableFieldLabelName: string) =>
        updateTables(
            convertFieldsToEnabledFieldInputs(
                fields.map((field) =>
                    field === table
                        ? {
                              ...field,
                              options: field.options.map((option) =>
                                  option.name === 'field_label_name'
                                      ? { ...option, value: tableFieldLabelName }
                                      : option
                              ),
                          }
                        : field
                )
            )
        );

    const handleDeleteTable = (table: FieldDefinition) =>
        updateTables(
            convertFieldsToEnabledFieldInputs(
                fields.filter((field) => field !== table && !field.name.startsWith(`${table.name}${GROUP_SEPARATOR}`))
            )
        );

    const sections: FieldsSection[] = [
        HEADER_SECTION,
        ...tables.map((table) => ({
            itemType: table.name,
            title: tableLabelOf(table),
            menuItems: (
                <>
                    <DropdownMenu.Item onSelect={() => setTableModal({ kind: 'rename', table })}>
                        {t('configPage.document.universal.tableActions.rename')}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item danger onSelect={() => setTableModal({ kind: 'delete', table })}>
                        {t('configPage.document.universal.tableActions.delete')}
                    </DropdownMenu.Item>
                </>
            ),
        })),
    ];

    return (
        <>
            <FieldsPage
                sections={sections}
                config={config}
                documentConfiguration={documentConfiguration}
                channelId={channelId}
                hasFineTuning={false}
                onSubmit={handleSubmit}
                customFieldOptions={UNIVERSAL_CUSTOM_FIELD_OPTIONS}
                footer={
                    <Button
                        type="button"
                        variant="outline"
                        className="self-start"
                        onClick={() => setTableModal({ kind: 'add' })}
                    >
                        <AddIcon /> {t('configPage.document.universal.addNewItemType')}
                    </Button>
                }
            />

            {tableModal?.kind === 'add' && (
                <UniversalItemTypeFormModal
                    title={t('configPage.document.universal.modal.addTitle')}
                    submitButtonLabel={t('configPage.document.universal.modal.addButton')}
                    takenNames={fields.map((field) => field.name).filter((name) => !name.includes(GROUP_SEPARATOR))}
                    onClose={() => setTableModal(null)}
                    onSubmit={handleAddTable}
                />
            )}

            {tableModal?.kind === 'rename' && (
                <UniversalItemTypeFormModal
                    title={t('configPage.document.universal.modal.editTitle')}
                    submitButtonLabel={t('configPage.document.universal.modal.editButton')}
                    onClose={() => setTableModal(null)}
                    onSubmit={({ tableFieldLabelName }) => handleRenameTable(tableModal.table, tableFieldLabelName)}
                    data={{
                        tableDataLabelName: optionValue(tableModal.table, 'data_label_name') ?? tableModal.table.name,
                        tableFieldLabelName: tableLabelOf(tableModal.table),
                    }}
                />
            )}

            {tableModal?.kind === 'delete' && (
                <ConfirmModal
                    title={t('configPage.document.universal.deleteModal.title', {
                        tableName: tableLabelOf(tableModal.table),
                    })}
                    labelConfirm={t('configPage.document.universal.deleteModal.confirmButton')}
                    danger
                    onCancel={() => setTableModal(null)}
                    onConfirm={() => handleDeleteTable(tableModal.table)}
                >
                    <p>{t('configPage.document.universal.deleteModal.message')}</p>
                </ConfirmModal>
            )}
        </>
    );
};

export default UniversalFieldsConfig;
